import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Upload, Trash2, X, ImagePlus, AlertCircle } from 'lucide-react';
import { assetsApi, type ImageAsset } from '../lib/api';

const MAX_PHOTOS = 6;

export default function PhotosPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [photos, setPhotos] = useState<ImageAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadPhotos();
  }, []);

  async function loadPhotos() {
    try {
      const { assets } = await assetsApi.list({ kind: 'user_photo' });
      setPhotos(assets);
    } catch {
      setError('Impossible de charger les photos');
    } finally {
      setLoading(false);
    }
  }

  async function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    if (photos.length >= MAX_PHOTOS) {
      setError(`Limite atteinte. Supprimez une photo pour en ajouter une nouvelle.`);
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const { asset } = await assetsApi.upload(file, undefined, 'user_photo', 'upload');
      setPhotos((prev) => [asset, ...prev]);
    } catch (err: any) {
      setError(err.message || 'Erreur lors de l\'upload');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function handleDelete(id: string) {
    try {
      await assetsApi.delete(id);
      setPhotos((prev) => prev.filter((p) => p.id !== id));
      setDeleteId(null);
    } catch {
      setError('Impossible de supprimer la photo');
    }
  }

  const canUpload = photos.length < MAX_PHOTOS;

  return (
    <div className="min-h-screen flex flex-col safe-area-inset">
      {/* Header */}
      <header className="flex items-center gap-4 px-6 pt-4 pb-4">
        <button onClick={() => navigate(-1)} className="text-white/40 hover:text-white/70 transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-sm font-medium text-white/70">Mes photos</h1>
      </header>

      <main className="flex-1 px-5 pb-28 overflow-y-auto">
        {/* Counter */}
        <div className="flex items-center justify-between mb-5 px-1">
          <p className="text-xs text-white/40">
            {photos.length} / {MAX_PHOTOS} photos
          </p>
          <div className="flex gap-1">
            {Array.from({ length: MAX_PHOTOS }).map((_, i) => (
              <div
                key={i}
                className="w-2 h-2 rounded-full"
                style={{
                  background: i < photos.length ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.1)',
                }}
              />
            ))}
          </div>
        </div>

        {/* Error */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              className="flex items-start gap-2 p-3 rounded-xl mb-4"
              style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.15)' }}
            >
              <AlertCircle className="w-4 h-4 text-red-400/60 mt-0.5 shrink-0" />
              <p className="text-xs text-red-400/80">{error}</p>
              <button onClick={() => setError(null)} className="ml-auto shrink-0">
                <X className="w-3 h-3 text-red-400/40" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Upload button */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={handleUpload}
        />

        {canUpload && (
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="w-full flex items-center justify-center gap-3 p-4 rounded-2xl mb-5 transition-all"
            style={{
              background: 'rgba(255,255,255,0.04)',
              border: '1.5px dashed rgba(255,255,255,0.15)',
            }}
          >
            {uploading ? (
              <div className="w-5 h-5 border-2 border-white/20 border-t-white/60 rounded-full animate-spin" />
            ) : (
              <ImagePlus className="w-5 h-5 text-white/30" />
            )}
            <span className="text-sm text-white/40">
              {uploading ? 'Chargement...' : 'Ajouter une photo'}
            </span>
          </button>
        )}

        {!canUpload && (
          <div
            className="flex items-center gap-3 p-4 rounded-2xl mb-5"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
          >
            <Upload className="w-4 h-4 text-white/20" />
            <p className="text-xs text-white/35">
              Limite de {MAX_PHOTOS} photos atteinte. Supprimez-en une pour pouvoir en ajouter une nouvelle.
            </p>
          </div>
        )}

        {/* Photos grid */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-6 h-6 border-2 border-white/10 border-t-white/40 rounded-full animate-spin" />
          </div>
        ) : photos.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <ImagePlus className="w-10 h-10 text-white/10" />
            <p className="text-sm text-white/30">Aucune photo ajoutée</p>
            <p className="text-xs text-white/20">Ajoutez jusqu'à {MAX_PHOTOS} photos de référence</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <AnimatePresence>
              {photos.map((photo) => (
                <motion.div
                  key={photo.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  className="relative aspect-square rounded-2xl overflow-hidden"
                  style={{ background: 'rgba(255,255,255,0.05)' }}
                >
                  <img
                    src={photo.url}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                  <button
                    onClick={() => setDeleteId(photo.id)}
                    className="absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center"
                    style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(8px)' }}
                  >
                    <Trash2 className="w-3.5 h-3.5 text-white/70" />
                  </button>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}

        <p className="text-center text-xs text-white/20 mt-8">
          Ces photos servent de référence pour la génération d'images
        </p>
      </main>

      {/* Delete confirmation */}
      <AnimatePresence>
        {deleteId && (
          <div className="fixed inset-0 z-50 flex items-center justify-center px-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setDeleteId(null)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="glass-card-elevated p-6 w-full max-w-sm relative z-10 space-y-4"
            >
              <h3 className="text-base font-medium text-center">Supprimer cette photo ?</h3>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteId(null)}
                  className="flex-1 btn-glass py-3 text-sm"
                >
                  Annuler
                </button>
                <button
                  onClick={() => handleDelete(deleteId)}
                  className="flex-1 py-3 rounded-xl text-sm font-medium"
                  style={{ background: 'rgba(239,68,68,0.15)', color: '#f87171', border: '1px solid rgba(239,68,68,0.2)' }}
                >
                  Supprimer
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
